/**
 * ChatSettingsPopup.tsx
 *
 * Lightweight modal popup for chat font size, opacity, and dock position reset.
 */

import React from 'react';
import { ChatPrefs } from '../chatPrefs';
import { ACCENT, COLORS, FONTS, RADII, SPACE } from '../../theme';

interface ChatSettingsPopupProps {
  prefs: ChatPrefs;
  onUpdatePrefs: (next: ChatPrefs) => void;
  onResetPosition: () => void;
  onClose: () => void;
}

export const ChatSettingsPopup: React.FC<ChatSettingsPopupProps> = ({
  prefs,
  onUpdatePrefs,
  onResetPosition,
  onClose,
}) => {
  return (
    <div
      data-testid="chat-settings-popup"
      style={popupStyles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={popupStyles.panel}>
        <div style={popupStyles.header}>
          <span style={popupStyles.title}>Chat Settings</span>
          <button
            onClick={onClose}
            style={popupStyles.closeBtn}
            title="Close Settings"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Font size row */}
        <div style={popupStyles.section}>
          <div style={popupStyles.sectionLabel}>Text Size</div>
          <div style={popupStyles.buttonRow}>
            {(['compact', 'normal', 'large'] as const).map((size) => (
              <button
                key={size}
                data-testid={`chat-font-size-${size}`}
                onClick={() => onUpdatePrefs({ ...prefs, fontSize: size })}
                style={popupStyles.pillButton(prefs.fontSize === size)}
              >
                {size.charAt(0).toUpperCase() + size.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Opacity row */}
        <div style={popupStyles.section}>
          <div style={popupStyles.sectionLabel}>Background Opacity</div>
          <div style={popupStyles.buttonRow}>
            {[
              { val: 0.35, label: '35%' },
              { val: 0.55, label: '55%' },
              { val: 0.8, label: '80%' },
              { val: 0.95, label: 'Solid' },
            ].map(({ val, label }) => (
              <button
                key={val}
                data-testid={`chat-opacity-${val}`}
                onClick={() => onUpdatePrefs({ ...prefs, opacity: val })}
                style={popupStyles.pillButton(prefs.opacity === val)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Reset position button */}
        <div style={{ marginTop: SPACE.sm, borderTop: '1px solid rgba(196, 164, 105, 0.2)', paddingTop: SPACE.sm }}>
          <button
            data-testid="chat-reset-position"
            onClick={() => {
              onResetPosition();
              onClose();
            }}
            style={popupStyles.resetButton}
          >
            ↺ Reset Dock Position
          </button>
        </div>
      </div>
    </div>
  );
};

const popupStyles = {
  overlay: {
    position: 'absolute' as const,
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
    backdropFilter: 'blur(2px)',
  },
  panel: {
    backgroundColor: 'rgba(12, 16, 24, 0.96)',
    border: `1px solid ${ACCENT.brass}`,
    borderRadius: RADII.md,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.8)',
    padding: '12px 14px',
    width: 260,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
    fontFamily: FONTS.ui,
    color: COLORS.textPrimary,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid rgba(196, 164, 105, 0.25)',
    paddingBottom: '6px',
  },
  title: {
    fontFamily: FONTS.display,
    fontSize: '12.5px',
    fontWeight: 'bold',
    color: ACCENT.brassBright,
    letterSpacing: '0.04em',
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    color: COLORS.textMuted,
    fontSize: '12px',
    cursor: 'pointer',
    padding: '2px 4px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  sectionLabel: {
    fontSize: '10.5px',
    color: COLORS.textMuted,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  buttonRow: {
    display: 'flex',
    gap: '4px',
  },
  pillButton: (active: boolean) => ({
    flex: 1,
    padding: '4px 6px',
    fontSize: '10.5px',
    fontWeight: active ? 'bold' : 'normal',
    borderRadius: RADII.sm,
    border: `1px solid ${active ? ACCENT.brassBright : 'rgba(255, 255, 255, 0.1)'}`,
    background: active ? 'rgba(196, 164, 105, 0.25)' : 'rgba(255, 255, 255, 0.04)',
    color: active ? '#ffffff' : '#9ca3af',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  }),
  resetButton: {
    width: '100%',
    padding: '6px 8px',
    fontSize: '11px',
    fontWeight: 'bold',
    borderRadius: RADII.sm,
    border: '1px solid rgba(196, 164, 105, 0.4)',
    background: 'rgba(196, 164, 105, 0.12)',
    color: ACCENT.brassBright,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
};
